import { useState, useEffect, useCallback } from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Cookies from "js-cookie";
import { wrapper } from "@lib/store";
import { useCurrentUser, useProductsRented, useProductsLiked } from "@hooks/use-current-user";
import {
  useCatalogNavigation,
  useClearCatalogLastScrollY,
} from "@hooks/use-catalog-navigation-hooks";
import Catalog from "@components/views/Catalog";
import UserListHeader from "@components/views/Catalog/Wishlist/UserListHeader";
import UserListSuggestedProducts from "@components/views/Catalog/Wishlist/UserListSuggestedProducts";
import isServer from "@utils/is-server";
import catalogContexts from "@utils/constants/catalogContexts";
import { getUserList } from "@utils/call-api/userlists-call";
import UserListNotFound from "@components/views/Catalog/Wishlist/UserListNotFound";
import useContentLoaderAnimation from "@hooks/use-content-loader-animation";
import CustomLoader from "@components/core/CustomLoader";

const EmptyComponent = ({ children }) => <>{children}</>;

const UserlistPage = ({ hub, context, page, url, userListUuid, typeOfGridCookie, serverState }) => {
  const user = useCurrentUser();
  const productsRented = useProductsRented();
  const productsLiked = useProductsLiked();
  const catalogNavigation = useCatalogNavigation();
  const clearCatalogLastScrollY = useClearCatalogLastScrollY();

  const [userList, setUserList] = useState(null);
  const [userListNotFound, setUserListNotFound] = useState(false);

  const isFirstPage = page === "1" || !page;

  const Container = EmptyComponent;

  function canDisplayCatalog() {
    if (isServer()) return false;
    if (!user && Cookies.get("jwt-token")) return false;
    if (!productsRented && Cookies.get("jwt-token")) return false;
    if (!productsLiked && Cookies.get("jwt-token")) return false;
    if (!userList) return false;

    return true;
  }

  const fetchUserList = useCallback(async () => {
    try {
      const res = await getUserList(userListUuid);
      setUserList(res);
    } catch {
      setUserListNotFound(true);
    }
  }, [userListUuid]);

  useEffect(() => {
    setUserListNotFound(false);
    fetchUserList();
  }, [userListUuid, fetchUserList]);

  const loaderShowDuration = 750;
  const { contentProps, loaderProps } = useContentLoaderAnimation(loaderShowDuration);

  useEffect(() => {
    setTimeout(() => {
      if (isServer()) return;

      window.scrollTo({ top: catalogNavigation?.lastScrollY, behavior: "instant" });
      clearCatalogLastScrollY();
    }, loaderShowDuration);
  }, []);

  return (
    <Container {...serverState}>
      {userListNotFound ? (
        <UserListNotFound />
      ) : (
        <>
          <div {...loaderProps}>
            <CustomLoader
              variant="Primary"
              style={{ width: "140px" }}
            />
          </div>
          <div {...contentProps}>
            <UserListHeader
              userList={userList}
              fetchUserList={fetchUserList}
            />
            {canDisplayCatalog() && (
              <Catalog
                context={context}
                hub={hub}
                seoContent={isFirstPage ? hub.seo_content : ""}
                url={url}
                typeOfGridCookie={typeOfGridCookie}
                userListProducts={userList?.product_ids}
                onProductRemovedFromUserList={fetchUserList}
                noHighlights
              />
            )}
            {canDisplayCatalog() && userList?.editable && (
              <UserListSuggestedProducts
                userListUuid={userList.uuid}
                fetchUserList={fetchUserList}
              />
            )}
          </div>
        </>
      )}
    </Container>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  () =>
    async ({ req, query, locale }) => {
      const hub = { slug: `userlist-${query.uuid}` };
      const context = catalogContexts.userlist;

      const protocol = req.headers.referer?.split("://")[0] || "https";
      const url = `${protocol}://${req.headers.host}${req.url}`;

      const typeOfGridCookie = req.cookies["type_of_catalog_grid"] || "small-pictures";

      const props = {
        hub,
        context,
        page: query.page || "",
        url,
        userListUuid: query.uuid,
        typeOfGridCookie,
        headerBehaviors: { noHead: true, context },
      };

      return {
        props: {
          ...props,
          ...(await serverSideTranslations(locale, ["common", "catalog", "feedbacks"])),
        },
      };
    },
);

UserlistPage.requireAuth = false;

export default UserlistPage;
